import { useEffect, useState } from 'react';
import './App.scss';
import { About, CodeLab, Footer, Header, Skills, Work } from './container';
import Navbar from './components/Navbar/Navbar';
import CustomCursor from './components/CustomCursor/CustomCursor';
import SEO from './components/SEO/SEO';

const THEME_STORAGE_KEY = 'usama-portfolio-theme';

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';

  const documentTheme = document.documentElement.dataset.theme;
  if (documentTheme === 'light' || documentTheme === 'dark') return documentTheme;

  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
  } catch (error) {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const scrollToTop = (event) => {
  event.preventDefault();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
};

function App() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    const colorScheme = window.matchMedia('(prefers-color-scheme: dark)');
    const followSystemTheme = (event) => {
      try {
        if (window.localStorage.getItem(THEME_STORAGE_KEY)) return;
      } catch (error) {
        // Continue following the system preference if storage is unavailable.
      }

      setTheme(event.matches ? 'dark' : 'light');
    };

    if (colorScheme.addEventListener) {
      colorScheme.addEventListener('change', followSystemTheme);
      return () => colorScheme.removeEventListener('change', followSystemTheme);
    }

    colorScheme.addListener(followSystemTheme);
    return () => colorScheme.removeListener(followSystemTheme);
  }, []);

  const toggleTheme = () => {
    setTheme((currentTheme) => {
      const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';

      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch (error) {
        // The in-memory preference still works for the current session.
      }

      return nextTheme;
    });
  };

  return (
    <div className="app">
      <SEO theme={theme} />
      <CustomCursor />
      <Navbar theme={theme} onToggleTheme={toggleTheme} />
      <Header />
      <About />
      <Work />
      <CodeLab />
      <Skills />
      <Footer />

      <footer className="app__copyright">
        <div className="app__copyright-inner">
          <span className="app__copyright-brand" aria-hidden="true">
            US<i>/</i>Portfolio
          </span>
          <p>
            Designed &amp; built by <span>Usama Sajid</span> — Dubai, UAE
          </p>
          <div className="app__copyright-end">
            <p>&copy; {new Date().getFullYear()} All rights reserved</p>
            <a href="#home" onClick={scrollToTop}>
              Back to top <span aria-hidden="true">&uarr;</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
